import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { css } from "@/styled-system/css";
import { Card, Box, Collapsible, TextButton, Icon, Divider, Heading, Paragraph } from "@zillow/constellation-10";
import { IconChevronUpOutline, IconChevronDownOutline } from "@zillow/constellation-icons";
import React, { useContext, useMemo } from "react";
import { baseExpenses, growthRates, buyingExpenses, rentalExpenses } from "../../RentVsBuyCalculator.labels.esm.js";
import { trackClickstreamInteraction } from "../../RentVsBuyCalculator.tracking.esm.js";
import { calculateMaxValForSlider, calculateStepForSlider, calculatedUpdatesForAVBInputs } from "../../RentVsBuyCalculator.utils.esm.js";
import { DataContext } from "../../RentVsBuyDataProvider.esm.js";
import CustomSlider from "../CustomSlider/CustomSlider.esm.js";
import RentVsBuyInputField from "../InputField/InputField.esm.js";
const scrollSectionCard = css.raw({
  padding: "token(spacing.400)",
  width: "100%"
});
const expensesBox = css.raw({
  display: "flex",
  flexDirection: "column",
  gap: "token(spacing.200)",
  marginBottom: "token(spacing.400)"
});
const CustomScrollSection = () => {
  const {
    currentState,
    setCurrentState
  } = useContext(DataContext);
  const updateSliderValue = (e, id) => {
    const stateKey = id;
    if (stateKey) {
      setCurrentState((prevState) => ({
        ...prevState,
        [stateKey]: e
      }));
    }
  };
  const trackSliderClickstreamInteraction = (id) => {
    trackClickstreamInteraction(id, currentState);
  };
  const updateInputValue = (currentValue, id) => {
    const calculatedUpdates = calculatedUpdatesForAVBInputs(currentState);
    const stateKey = id;
    const newState = {
      ...currentState
    };
    if (stateKey) {
      newState[stateKey] = currentValue;
    }
    if (calculatedUpdates[id]) {
      const {
        stateKey: calcKey,
        calculation
      } = calculatedUpdates[id];
      newState[calcKey] = calculation(currentValue);
    }
    setCurrentState(newState);
    trackClickstreamInteraction(stateKey, newState);
  };
  const baseExpensesList = useMemo(() => {
    return baseExpenses(calculateStepForSlider, calculateMaxValForSlider, currentState).map((input) => /* @__PURE__ */ jsx(CustomSlider, { id: input.id, min: 0, step: calculateStepForSlider(input.id, calculateMaxValForSlider(input.id, input.percentageQuestion)), max: calculateMaxValForSlider(input.id, input.percentageQuestion), percentageQuestion: input.percentageQuestion, defaultValue: input.defaultValue, sliderQuestion: input.sliderQuestion, toolTipHeader: input.toolTipHeader, toolTipStatement: input.toolTipStatement, onChange: (e) => updateSliderValue(e, input.id), onAfterChange: () => trackSliderClickstreamInteraction(input.id) }, input.id));
  }, [currentState]);
  const growthExpensesList = useMemo(() => {
    return growthRates(calculateStepForSlider, calculateMaxValForSlider, currentState).map((input) => /* @__PURE__ */ jsx(CustomSlider, { id: input.id, min: 0, step: calculateStepForSlider(input.id, calculateMaxValForSlider(input.id, input.percentageQuestion)), max: calculateMaxValForSlider(input.id, input.percentageQuestion), defaultValue: input.defaultValue, percentageQuestion: input.percentageQuestion, sliderQuestion: input.sliderQuestion, toolTipHeader: input.toolTipHeader, toolTipStatement: input.toolTipStatement, onChange: (e) => updateSliderValue(e, input.id), onAfterChange: () => trackSliderClickstreamInteraction(input.id) }, input.id));
  }, [currentState]);
  const buyingExpensesList = useMemo(() => {
    return buyingExpenses(currentState).map((input) => /* @__PURE__ */ jsx(RentVsBuyInputField, { id: input.id, inputLabel: input.inputLabel, value: input.defaultValue, percentageQuestion: input.percentageQuestion, perMonth: input.perMonth, onChange: (e) => {
      const val = e.target.value ? e.target.value : "0";
      updateInputValue(parseFloat(val), input.id);
    }, toolTipHeader: input.toolTipHeader, toolTipStatement: input.toolTipStatement }, input.id));
  }, [currentState]);
  const rentingExpensesList = useMemo(() => {
    return rentalExpenses(currentState).map((input) => /* @__PURE__ */ jsx(RentVsBuyInputField, { id: input.id, inputLabel: input.inputLabel, value: input.defaultValue, percentageQuestion: input.percentageQuestion, perMonth: input.perMonth, onChange: (e) => {
      const val = e.target.value ? e.target.value : "0";
      updateInputValue(parseFloat(val), input.id);
    }, toolTipHeader: input.toolTipHeader, toolTipStatement: input.toolTipStatement }, input.id));
  }, [currentState]);
  return /* @__PURE__ */ jsxs(Card.Root, { css: scrollSectionCard, children: [
    /* @__PURE__ */ jsx(Box, { css: expensesBox, children: baseExpensesList }),
    /* @__PURE__ */ jsx(Collapsible, { children: (collapsed, toggle) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
      /* @__PURE__ */ jsx(Box, { css: expensesBox, children: /* @__PURE__ */ jsxs(TextButton.Root, { tone: "brand", onClick: toggle, children: [
        /* @__PURE__ */ jsx(TextButton.Label, { children: collapsed ? "Less" : "More" }),
        /* @__PURE__ */ jsx(TextButton.Icon, { children: collapsed ? /* @__PURE__ */ jsx(Icon, { children: /* @__PURE__ */ jsx(IconChevronUpOutline, {}) }) : /* @__PURE__ */ jsx(Icon, { children: /* @__PURE__ */ jsx(IconChevronDownOutline, {}) }) })
      ] }) }),
      collapsed ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(Box, { css: expensesBox, children: [
          /* @__PURE__ */ jsx(Divider, {}),
          /* @__PURE__ */ jsx(Heading, { level: 5, children: "OUR ASSUMPTIONS" }),
          /* @__PURE__ */ jsx(Paragraph, { children: "Below are other factors we've taken into consideration. They're pre-filled with the averages for your location and can be adjusted to match your situation." })
        ] }),
        /* @__PURE__ */ jsxs(Box, { css: expensesBox, children: [
          /* @__PURE__ */ jsx(Heading, { level: 6, children: "Growth Rates" }),
          growthExpensesList
        ] }),
        /* @__PURE__ */ jsxs(Box, { css: expensesBox, children: [
          /* @__PURE__ */ jsx(Heading, { level: 6, children: "Buying Expenses" }),
          buyingExpensesList
        ] }),
        /* @__PURE__ */ jsxs(Box, { css: expensesBox, children: [
          /* @__PURE__ */ jsx(Heading, { level: 6, children: "Rental Expenses" }),
          rentingExpensesList
        ] })
      ] }) : null
    ] }) })
  ] });
};
export {
  CustomScrollSection as default
};
